/**
 * New-level wizard (Epic C) — the "+ New level" modal. Optionally takes a brief
 * ("Make a fire level!"), has the AI pitch a concept that REUSES existing Content
 * mechanics and proposes NEW ones, writes accepted new mechanics into the Content
 * design doc, then generates the course against them (validate-and-repair loop).
 * Blank brief = generate from the existing design; "Start blank" = hand-author.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignProject, LevelEntry } from '@volley/crucible-ipc-types';
import type { ThreeCsProfile } from '@volley/crucible-levels';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  pitchLevel,
  pitchCampaign,
  applyNewMechanics,
  type LevelPitch,
  type CampaignPitch,
} from './level-wizard';
import { generateLevelFromDesign } from './level-generate';
import {
  kindForArchetype,
  coerceEnabledKind,
  AVAILABLE_LEVEL_KINDS,
  LEVEL_KIND_LABELS,
  type LevelKindChoice,
} from './level-defaults';
import { useCrucibleClient } from '../../transport/context.js';

type Stage = 'ask' | 'pitching' | 'pitch' | 'generating';

export interface NewLevelWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  project: DesignProject;
  threeCs: ThreeCsProfile;
  saveLevel: (level: LevelEntry) => Promise<LevelEntry>;
  /** Existing levels + progression — context for coherent slotting. */
  levels?: LevelEntry[];
  progression?: unknown;
  /** The campaign slot the new level will occupy (when a progression is authored). */
  slot?: { label: string; index: number; total: number };
  /** Open the created level in the editor. `note` reports residual issues (if any). */
  onCreated: (level: LevelEntry, note: string | null) => void;
  /** Campaign result — all created levels in order (host appends progression in one save). */
  onCreatedMany?: (levels: LevelEntry[], note: string | null) => void;
  /** Old behavior: a blank starter track for hand-authoring. */
  onStartBlank: () => void;
  /** Fired after new mechanics were written into the Content doc (staleness etc.). */
  onContentChanged: (added: number) => void;
}

export function NewLevelWizard({
  open,
  onOpenChange,
  gameId,
  project,
  threeCs,
  saveLevel,
  levels,
  progression,
  slot,
  onCreated,
  onCreatedMany,
  onStartBlank,
  onContentChanged,
}: NewLevelWizardProps): ReactElement {
  const client = useCrucibleClient();
  const [stage, setStage] = useState<Stage>('ask');
  const [brief, setBrief] = useState('');
  const [pitch, setPitch] = useState<LevelPitch | null>(null);
  /** The kind the level will be created as — AI-proposed, user-overridable. */
  const [kind, setKind] = useState<LevelKindChoice>(
    coerceEnabledKind(kindForArchetype(threeCs.archetype)),
  );
  const [campaignOn, setCampaignOn] = useState(false);
  const [campaignCount, setCampaignCount] = useState(5);
  const [campaign, setCampaign] = useState<CampaignPitch | null>(null);
  const [progressNote, setProgressNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStage('ask');
      setBrief('');
      setPitch(null);
      setKind(coerceEnabledKind(kindForArchetype(threeCs.archetype)));
      setCampaign(null);
      setCampaignOn(false);
      setProgressNote(null);
      setError(null);
    }
    // Reset on open only — mid-flow archetype changes shouldn't clobber a pitch.
  }, [open]);

  const busy = stage === 'pitching' || stage === 'generating';

  const doPitch = (): void => {
    setStage('pitching');
    setError(null);
    const p = campaignOn
      ? pitchCampaign(client, {
          gameId,
          project,
          brief: brief.trim(),
          count: campaignCount,
          archetype: threeCs.archetype,
        }).then((c) => setCampaign(c))
      : pitchLevel(client, {
          gameId,
          project,
          brief: brief.trim(),
          archetype: threeCs.archetype,
        }).then((lp) => {
          setPitch(lp);
          if (lp.kind) setKind(coerceEnabledKind(lp.kind));
        });
    p.then(() => setStage('pitch')).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
      setStage('ask');
    });
  };

  const doGenerateCampaign = (arc: CampaignPitch): void => {
    setStage('generating');
    setError(null);
    const run = async (): Promise<void> => {
      const created: LevelEntry[] = [];
      let residual = 0;
      const startIndex = slot?.index ?? 0;
      const total = startIndex + arc.levels.length;
      for (let i = 0; i < arc.levels.length; i++) {
        const concept = arc.levels[i];
        setProgressNote(`Building "${concept.name}" (${i + 1} of ${arc.levels.length})…`);
        const res = await generateLevelFromDesign(client, {
          gameId,
          project,
          threeCs,
          save: saveLevel,
          brief: concept.brief,
          pitch: { name: concept.name, summary: concept.summary, mechanics: [] },
          levels: [...(levels ?? []), ...created],
          progression,
          slot: { label: String(startIndex + i + 1), index: startIndex + i, total },
        });
        created.push(res.level);
        residual += res.findings.length;
      }
      onOpenChange(false);
      onCreatedMany?.(
        created,
        residual > 0 ? `${residual} issue(s) remain across the campaign — see Validation.` : null,
      );
    };
    run().catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
      setStage('pitch');
    });
  };

  const doGenerate = (accepted: LevelPitch): void => {
    setStage('generating');
    setError(null);
    const run = async (): Promise<void> => {
      const added = await applyNewMechanics(client, { gameId, project, pitch: accepted });
      if (added > 0) onContentChanged(added);
      const res = await generateLevelFromDesign(client, {
        gameId,
        project,
        threeCs,
        kind,
        save: saveLevel,
        brief: brief.trim() || undefined,
        pitch: accepted,
        levels,
        progression,
        slot,
      });
      onOpenChange(false);
      onCreated(
        res.level,
        res.findings.length > 0
          ? `Generated with ${res.findings.length} issue(s) left after ${res.attempts} tries — see Validation.`
          : null,
      );
    };
    run().catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
      setStage('pitch');
    });
  };

  const newMechanics = pitch?.mechanics.filter((m) => m.status === 'new') ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New level</DialogTitle>
        </DialogHeader>

        {stage === 'ask' && (
          <div className="space-y-3">
            <p className="text-sm text-crucible-muted">
              What kind of level? The AI pitches a concept first — you&rsquo;ll see exactly which
              existing mechanics it uses and which new ones it would add to your Content doc before
              anything is created. Leave blank and it pitches its own idea from the design.
            </p>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder={'"Make a fire level!" (optional)'}
              rows={3}
              autoFocus
              className="w-full rounded border border-crucible-border bg-crucible-bg p-2 text-sm text-crucible-fg outline-none focus:border-crucible-accent"
            />
            <div className="flex items-center gap-3 text-sm text-crucible-fg">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={campaignOn}
                  onChange={(e) => setCampaignOn(e.target.checked)}
                />
                <span className="text-crucible-muted">Whole campaign</span>
              </label>
              {campaignOn && (
                <label className="flex items-center gap-2 text-crucible-muted">
                  <input
                    type="number"
                    min={2}
                    max={10}
                    step={1}
                    value={campaignCount}
                    onChange={(e) =>
                      setCampaignCount(Math.max(2, Math.min(10, Number(e.target.value) || 5)))
                    }
                    className="w-16 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-0.5 text-right text-sm text-crucible-fg"
                  />
                  levels, escalating difficulty
                </label>
              )}
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
          </div>
        )}

        {stage === 'pitching' && (
          <p className="py-4 text-center text-sm text-crucible-muted">
            <span className="mr-2 inline-block size-2 animate-pulse rounded-full bg-amber-300" />
            Pitching a concept…
          </p>
        )}

        {stage === 'pitch' && campaign && (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-crucible-fg">{campaign.name}</p>
              <p className="text-sm text-crucible-muted">{campaign.summary}</p>
            </div>
            <ol className="space-y-1.5">
              {campaign.levels.map((l, i) => (
                <li key={l.name} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 w-5 shrink-0 text-center font-mono text-[11px] text-crucible-accent-hover">
                    {(slot?.index ?? 0) + i + 1}
                  </span>
                  <span className="min-w-0">
                    <span className="text-crucible-fg">{l.name}</span>
                    <span className="text-crucible-muted"> — {l.summary}</span>
                  </span>
                </li>
              ))}
            </ol>
            <p className="rounded border border-crucible-border bg-crucible-bg-elevated p-2 text-xs text-crucible-muted">
              Will create {campaign.levels.length} levels using existing Content mechanics and
              append them to the progression in order. Each is validated (and repaired) before it
              lands.
            </p>
            {error && <p className="text-xs text-red-400">{error}</p>}
          </div>
        )}

        {stage === 'pitch' && !campaign && pitch && (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-crucible-fg">{pitch.name}</p>
              <p className="text-sm text-crucible-muted">{pitch.summary}</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-crucible-muted">Level type</span>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as LevelKindChoice)}
                data-testid="wizard-kind-select"
                className="rounded border border-crucible-border bg-crucible-bg-elevated px-1.5 py-0.5 text-xs text-crucible-fg"
              >
                {AVAILABLE_LEVEL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {LEVEL_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
              {pitch.kind === kind && pitch.kindReason && (
                <span className="min-w-0 truncate text-crucible-muted">— {pitch.kindReason}</span>
              )}
            </div>
            <ul className="space-y-1.5">
              {pitch.mechanics.map((m) => (
                <li key={m.name} className="flex items-start gap-2 text-sm">
                  <span
                    className={`mt-0.5 shrink-0 rounded-full border px-1.5 text-[10px] uppercase ${
                      m.status === 'new'
                        ? 'border-crucible-accent/50 bg-crucible-accent/15 text-crucible-accent-hover'
                        : 'border-crucible-border text-crucible-muted'
                    }`}
                  >
                    {m.status}
                  </span>
                  <span className="min-w-0">
                    <span className="text-crucible-fg">{m.name}</span>
                    <span className="text-crucible-muted"> — {m.description}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="rounded border border-crucible-border bg-crucible-bg-elevated p-2 text-xs text-crucible-muted">
              Will use{' '}
              <span className="text-crucible-fg">
                {pitch.mechanics.length - newMechanics.length} existing
              </span>{' '}
              mechanic{pitch.mechanics.length - newMechanics.length === 1 ? '' : 's'}
              {newMechanics.length > 0 ? (
                <>
                  {' '}
                  and create{' '}
                  <span className="text-crucible-accent-hover">{newMechanics.length} new</span> (
                  {newMechanics.map((m) => m.name).join(', ')}) — written into your Content design
                  doc before the course generates.
                </>
              ) : (
                <> — nothing new added to Content.</>
              )}
            </p>
            {error && <p className="text-xs text-red-400">{error}</p>}
          </div>
        )}

        {stage === 'generating' && (
          <p className="py-4 text-center text-sm text-crucible-muted">
            <span className="mr-2 inline-block size-2 animate-pulse rounded-full bg-amber-300" />
            {progressNote ?? 'Building the level… (validated and repaired until it’s beatable)'}
          </p>
        )}

        <DialogFooter>
          {stage === 'ask' && (
            <>
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onStartBlank();
                }}
                className="mr-auto text-xs text-crucible-muted underline-offset-2 hover:text-crucible-fg hover:underline"
              >
                Start blank (hand-author)
              </button>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={doPitch}>{brief.trim() ? 'Pitch it' : 'Pitch something'}</Button>
            </>
          )}
          {stage === 'pitch' && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setCampaign(null);
                  setPitch(null);
                  setStage('ask');
                }}
              >
                Back
              </Button>
              {campaign ? (
                <Button onClick={() => doGenerateCampaign(campaign)}>
                  Create campaign ({campaign.levels.length} levels)
                </Button>
              ) : (
                <Button onClick={() => pitch && doGenerate(pitch)}>
                  {newMechanics.length > 0
                    ? `Create level + ${newMechanics.length} new mechanic${newMechanics.length > 1 ? 's' : ''}`
                    : 'Create level'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
